/**
 * Pure glue for the `/intake/*` route (ET-48/ET-49) — kept I/O-free so it's unit
 * testable without importing the route (which drags in Builder/RR client deps).
 *
 * Three concerns:
 *  - the First-Question → intake handoff contract (one shared type + reader), so
 *    the producing Builder block and the consuming route can't drift;
 *  - resolving which treatment a URL/authored entry maps to; and
 *  - reading the discovery-to-intake handoff context (ET-3/ET-14) — which page
 *    and CTA sent the shopper here.
 */
import { isSameOriginPath } from "~/lib/utils/auth-links";

import type { AnswerMap, AnswerValue } from "./schema";
import { TREATMENTS, type Treatment, treatmentBySlug } from "./treatments";

/**
 * Answers carried from the First-Question CTA into intake via RR navigation
 * state. The single source of truth for that wire shape — imported by both the
 * producer (first-question Builder block) and the consumer (intake route).
 *
 * A first-question module can now be a whole page group (many questions), so
 * the handoff carries a `questionId → value` map rather than one answer.
 */
export interface FirstQuestionHandoff {
  answers: AnswerMap;
  /**
   * ETH-91: the page that asked these answers, when it is not the intake (the
   * HRA landing page). Back from the intake's first step returns there.
   */
  returnTo?: string;
}

/**
 * Read + validate the handoff off `location.state`; `undefined` when
 * absent/malformed. Accepts the current `{ answers }` shape and normalizes the
 * legacy single-question `{ questionId, value }` shape into a one-entry map, so
 * an in-flight navigation from an older bundle still preselects correctly.
 */
export function readFirstQuestionHandoff(
  state: unknown,
): FirstQuestionHandoff | undefined {
  const raw = (state as { firstQuestion?: unknown } | null)?.firstQuestion;
  if (!raw || typeof raw !== "object") return undefined;

  const { answers, returnTo } = raw as {
    answers?: unknown;
    returnTo?: unknown;
  };
  if (answers && typeof answers === "object" && !Array.isArray(answers)) {
    // History state is client-writable: only a same-origin path is kept.
    return typeof returnTo === "string" && isSameOriginPath(returnTo)
      ? { answers: answers as AnswerMap, returnTo }
      : { answers: answers as AnswerMap };
  }

  const legacy = raw as { questionId?: unknown; value?: unknown };
  if (typeof legacy.questionId === "string") {
    return { answers: { [legacy.questionId]: legacy.value as AnswerValue } };
  }
  return undefined;
}

/** Last non-empty segment of an `/intake/*` splat (or path) → the treatment slug. */
export function lastSegment(splatOrPath: string): string {
  return splatOrPath.split("/").filter(Boolean).pop() ?? "";
}

/**
 * The authored `treatmentType` field wins over the URL slug (ET-48: business
 * user sets the treatment on the Builder entry); otherwise derive it from the
 * `/intake/*` splat so the route still works before any entry exists.
 */
export function resolveTreatmentSlug(
  treatmentType: string | undefined,
  splat: string,
): string {
  return (
    (typeof treatmentType === "string" && treatmentType) || lastSegment(splat)
  );
}

/**
 * Intake load-gate outcome for a resolved treatment slug (ET-12):
 *  - `ok`          — a known, available treatment; load its questionnaire.
 *  - `unavailable` — a known treatment that isn't available yet ("Coming Soon");
 *                    must NOT load a questionnaire.
 *  - `unknown`     — an invalid/unsupported slug.
 *
 * `unavailable` and `unknown` both mean "don't start intake"; the route keeps
 * them distinct only so it can show the right copy (a Coming Soon treatment
 * won't become loadable by retrying, unlike a transient load failure).
 */
export type IntakeTreatmentStatus = "ok" | "unavailable" | "unknown";

export interface ResolvedIntakeTreatment {
  treatment: Treatment | undefined;
  status: IntakeTreatmentStatus;
}

/**
 * Route-level availability gate (ET-12): resolves a slug to its treatment and a
 * load decision. The `/intake/*` route loads the questionnaire ONLY when the
 * status is `ok`, so a treatment that is unknown or has become unavailable
 * before intake loads shows the unavailable/error state rather than silently
 * continuing or loading a fixture. Mirrors the CTA-side `isTreatmentAvailable`
 * gate so the click path and the direct-navigation path agree. The route
 * passes the registry snapshot it resolved; the default is the code registry.
 */
export function resolveIntakeTreatment(
  slug: string | null | undefined,
  treatments: readonly Treatment[] = TREATMENTS,
): ResolvedIntakeTreatment {
  const treatment = treatmentBySlug(slug, treatments);
  if (!treatment) return { treatment: undefined, status: "unknown" };
  return { treatment, status: treatment.available ? "ok" : "unavailable" };
}

/**
 * `source`/`ctaLocation` are free-form, attacker-controllable query-string
 * input — `readIntakeEntryContext` does no validation by design (see below).
 * Any future consumer (analytics payload, DOM) must treat these as untrusted.
 */
export interface IntakeEntryContext {
  source?: string;
  ctaLocation?: string;
}

/**
 * Wire names for the entry-context query params. Producers (First-Question
 * block, future ET-12 CTA surfaces) and the consuming route both resolve
 * through these — never hardcode `src`/`cta` literals at a call site.
 */
const SOURCE_PARAM = "src";
const CTA_PARAM = "cta";

/**
 * Discovery-to-intake handoff context (ET-3/ET-14): which page and which CTA
 * sent the shopper into `/intake/*`. Plain query-string reads, no enum
 * validation — these values are consumed by analytics (ET-55/56), not
 * branching logic, so strict typing would only create a place for a
 * legit-but-unrecognized value to get silently dropped. Returns `undefined`
 * (not `""`) when absent so a future event payload can spread cleanly, e.g.
 * `...(source && { source })`.
 */
export function readIntakeEntryContext(
  searchParams: URLSearchParams,
): IntakeEntryContext {
  return {
    source: searchParams.get(SOURCE_PARAM) ?? undefined,
    ctaLocation: searchParams.get(CTA_PARAM) ?? undefined,
  };
}

/**
 * Symmetric writer for `readIntakeEntryContext` — producers build their
 * `/intake/*` query string through this so the wire names can't drift from
 * the reader. Returns `""` when there's nothing to carry.
 */
export function buildIntakeEntryQuery(context: IntakeEntryContext): string {
  const params = new URLSearchParams();
  if (context.source) params.set(SOURCE_PARAM, context.source);
  if (context.ctaLocation) params.set(CTA_PARAM, context.ctaLocation);
  return params.toString();
}
